import React, { useMemo, useState } from 'react';
import { Box, IconButton, InputAdornment, List, ListItem, ListSubheader, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { SoaFieldDef } from '../../types/formattedSoa';
import { DND_FIELD, DND_ROW_FIELD, GROUP_LABELS, GROUP_ORDER } from '../../utils/soaDesigner';

/**
 * The field catalog: "+" adds a field to the selected band (table fields become columns); a field can also
 * be dragged onto a band or onto the table.
 */

interface Props {
  fields: SoaFieldDef[];
  onAdd: (field: SoaFieldDef) => void;
  /** where "+" puts a non-table field, e.g. "Page header" */
  targetLabel: string;
}

const FieldSelector: React.FC<Props> = ({ fields, onAdd, targetLabel }) => {
  const [search, setSearch] = useState('');
  const groups = useMemo(() => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    const match = (f: SoaFieldDef) => words.every((w) => `${f.label} ${f.key}`.toLowerCase().includes(w));
    return GROUP_ORDER
      .map((g) => ({ group: g, items: fields.filter((f) => f.group === g && match(f)) }))
      .filter((g) => g.items.length > 0);
  }, [fields, search]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ p: 1, pb: 0.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search fields"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          inputProps={{ 'aria-label': 'Search fields' }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.3 }}>
          + adds to {targetLabel}; table fields become columns. Or drag a field onto the page.
        </Typography>
      </Box>
      <List dense disablePadding sx={{ overflow: 'auto', flex: 1, minHeight: 0 }} subheader={<li />}>
        {groups.map((g) => (
          <li key={g.group}>
            <ul style={{ padding: 0 }}>
              <ListSubheader sx={{ lineHeight: '28px', bgcolor: '#f5f7fa', fontSize: 12 }}>{GROUP_LABELS[g.group] ?? g.group}</ListSubheader>
              {g.items.map((f) => (
                <ListItem
                  key={f.key}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData(f.group === 'row' ? DND_ROW_FIELD : DND_FIELD, f.key);
                    e.dataTransfer.effectAllowed = 'copy';
                  }}
                  sx={{ pl: 0.5, pr: 0.5, cursor: 'grab', '&:hover': { bgcolor: 'action.hover' } }}
                  secondaryAction={(
                    <Tooltip title={f.group === 'row' ? 'Add as a table column' : `Add to ${targetLabel}`}>
                      <IconButton edge="end" size="small" aria-label={`Add ${f.label}`} onClick={() => onAdd(f)}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                >
                  <DragIndicatorIcon sx={{ fontSize: 14, color: 'text.disabled', mr: 0.5 }} />
                  <Box sx={{ minWidth: 0, pr: 3 }}>
                    <Typography variant="body2" noWrap title={f.label}>{f.label}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontFamily: 'monospace', fontSize: 10.5 }}>
                      {f.group === 'row' ? f.key : `{{${f.key}}}`}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </ul>
          </li>
        ))}
        {groups.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>No field matches.</Typography>}
      </List>
    </Box>
  );
};

export default FieldSelector;
